import React, { useState, useRef, useEffect } from 'react';
import { Play, Pause, SkipForward, SkipBack } from 'lucide-react';
import { Button } from './ui/button';

const AudioPlayer = ({ tracks, title }) => {
  const [currentTrackIndex, setCurrentTrackIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const audioRef = useRef(null);

  const currentTrack = tracks[currentTrackIndex];

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const updateTime = () => setCurrentTime(audio.currentTime);
    const updateDuration = () => setDuration(audio.duration);
    const handleEnded = () => handleNext();

    audio.addEventListener('timeupdate', updateTime);
    audio.addEventListener('loadedmetadata', updateDuration);
    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.removeEventListener('timeupdate', updateTime);
      audio.removeEventListener('loadedmetadata', updateDuration);
      audio.removeEventListener('ended', handleEnded);
    };
  }, [currentTrackIndex]);

  const togglePlay = () => {
    const audio = audioRef.current;
    if (isPlaying) {
      audio.pause();
    } else {
      audio.play();
    }
    setIsPlaying(!isPlaying);
  };

  const handleNext = () => {
    setCurrentTrackIndex((prev) => (prev + 1) % tracks.length);
    setIsPlaying(false);
  };

  const handlePrevious = () => {
    setCurrentTrackIndex((prev) => (prev - 1 + tracks.length) % tracks.length);
    setIsPlaying(false);
  };

  const handleSeek = (e) => {
    const audio = audioRef.current;
    const seekTime = (e.target.value / 100) * duration;
    audio.currentTime = seekTime;
    setCurrentTime(seekTime);
  };

  const formatTime = (time) => {
    if (isNaN(time)) return '0:00';
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  return (
    <div className="bg-gradient-to-br from-gray-900 to-black border border-orange-500/30 rounded-xl p-6 shadow-2xl">
      <audio ref={audioRef} src={currentTrack.audioUrl} />
      
      {title && (
        <h3 className="text-2xl font-bold text-orange-400 mb-4">{title}</h3>
      )}

      {/* Current Track Info */}
      <div className="mb-4">
        <h4 className="text-lg font-semibold text-white mb-1">{currentTrack.title}</h4>
        <p className="text-gray-400 text-sm">{currentTrack.artist}</p>
      </div>

      {/* Progress Bar */}
      <div className="mb-4">
        <input
          type="range"
          min="0"
          max="100"
          value={(currentTime / duration) * 100 || 0}
          onChange={handleSeek}
          className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer slider"
        />
        <div className="flex justify-between text-xs text-gray-400 mt-1">
          <span>{formatTime(currentTime)}</span>
          <span>{formatTime(duration)}</span>
        </div>
      </div>

      {/* Controls */}
      <div className="flex items-center justify-center gap-4">
        <Button
          onClick={handlePrevious}
          variant="ghost"
          size="icon"
          className="text-white hover:text-orange-400 hover:bg-orange-500/10"
        >
          <SkipBack className="h-6 w-6" />
        </Button>
        
        <Button
          onClick={togglePlay}
          size="icon"
          className="h-14 w-14 bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 rounded-full shadow-lg hover:shadow-orange-500/50 transition-all"
        >
          {isPlaying ? <Pause className="h-6 w-6" /> : <Play className="h-6 w-6 ml-1" />}
        </Button>
        
        <Button
          onClick={handleNext}
          variant="ghost"
          size="icon"
          className="text-white hover:text-orange-400 hover:bg-orange-500/10"
        >
          <SkipForward className="h-6 w-6" />
        </Button>
      </div>

      {/* Track List */}
      <div className="mt-6 space-y-2 max-h-48 overflow-y-auto">
        {tracks.map((track, index) => (
          <div
            key={track.id}
            onClick={() => {
              setCurrentTrackIndex(index);
              setIsPlaying(false);
            }}
            className={`p-3 rounded-lg cursor-pointer transition-all ${
              index === currentTrackIndex
                ? 'bg-orange-500/20 border border-orange-500/50'
                : 'bg-gray-800/50 hover:bg-gray-800 border border-transparent'
            }`}
          >
            <p className="text-white text-sm font-medium">{track.title}</p>
            <p className="text-gray-400 text-xs">{track.artist}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AudioPlayer;